import api from '@/lib/api';

export interface SLAPolicy {
  id: string;
  organization_id: string;
  name: string;
  description: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  rules_count?: string;
  active_tickets_count?: string;
}

export interface SLARule {
  id?: string;
  sla_policy_id?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  first_response_time: number;
  first_response_notify_at?: number;
  resolution_time: number;
  resolution_notify_at?: number;
  update_time?: number;
  update_notify_at?: number;
  business_hours_only: boolean;
}

export interface BusinessHours {
  id?: string;
  organization_id?: string;
  day_of_week: number; // 0 = Sunday, 6 = Saturday
  start_time: string;
  end_time: string;
  is_working_day: boolean;
}

export interface SLAMetrics {
  total_tickets: number;
  first_response_achieved: number;
  first_response_breached: number;
  resolution_achieved: number;
  resolution_breached: number;
  first_response_rate: number;
  resolution_rate: number;
  avg_first_response_minutes: number;
  avg_resolution_minutes: number;
}

export interface SLATicketStatus {
  id: string;
  ticket_id: string;
  sla_policy_id: string;
  policy_name: string;
  priority: string;
  first_response_target: string;
  resolution_target: string;
  first_response_at: string | null;
  resolution_at: string | null;
  first_response_status: 'pending' | 'achieved' | 'breached';
  resolution_status: 'pending' | 'achieved' | 'breached';
  first_response_breached: boolean;
  resolution_breached: boolean;
  is_paused: boolean;
  total_paused_duration: number;
  current_first_response_state: 'active' | 'responded' | 'paused' | 'breached';
  current_resolution_state: 'active' | 'resolved' | 'paused' | 'breached';
  first_response_remaining_seconds: number;
  resolution_remaining_seconds: number;
  target_first_response_minutes: number;
  target_resolution_minutes: number;
}

export interface Holiday {
  id: string;
  organization_id: string;
  date: string;
  name: string;
  is_recurring: boolean;
  created_at: string;
  updated_at: string;
}

export interface BreachedTicket {
  id: string;
  subject: string;
  priority: string;
  status: string;
  created_at: string;
  first_response_target: string;
  resolution_target: string;
  first_response_breached: boolean;
  resolution_breached: boolean;
  first_response_overdue_minutes: number;
  resolution_overdue_minutes: number;
}

export interface CreatePolicyData {
  name: string;
  description?: string;
  is_default?: boolean;
  rules?: SLARule[];
}

export interface UpdatePolicyData extends CreatePolicyData {
  is_active?: boolean;
}

class SLAService {
  // Get all SLA policies
  async getPolicies(organizationId?: string): Promise<{ success: boolean; data: SLAPolicy[] }> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await api.get(`/sla/policies${params}`);
    return response.data;
  }

  // Get single policy with rules
  async getPolicyById(id: string, organizationId?: string): Promise<{ success: boolean; data: { policy: SLAPolicy; rules: SLARule[] } }> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await api.get(`/sla/policies/${id}${params}`);
    return response.data;
  }

  // Create new policy
  async createPolicy(data: CreatePolicyData, organizationId?: string): Promise<{ success: boolean; data: SLAPolicy }> {
    const payload = organizationId ? { ...data, organization_id: organizationId } : data;
    const response = await api.post('/sla/policies', payload);
    return response.data;
  }

  // Update policy
  async updatePolicy(id: string, data: UpdatePolicyData, organizationId?: string): Promise<{ success: boolean; data: SLAPolicy }> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await api.put(`/sla/policies/${id}${params}`, data);
    return response.data;
  }

  // Delete policy
  async deletePolicy(id: string, organizationId?: string): Promise<{ success: boolean }> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await api.delete(`/sla/policies/${id}${params}`);
    return response.data;
  }

  // Get business hours
  async getBusinessHours(organizationId?: string): Promise<{ success: boolean; data: BusinessHours[] }> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await api.get(`/sla/business-hours${params}`);
    return response.data;
  }

  // Update business hours
  async updateBusinessHours(hours: BusinessHours[], organizationId?: string): Promise<{ success: boolean }> {
    const response = await api.put('/sla/business-hours', {
      business_hours: hours,
      organization_id: organizationId
    });
    return response.data;
  }

  // Get SLA metrics
  async getMetrics(period: number = 30, organizationId?: string): Promise<{ success: boolean; data: SLAMetrics }> {
    const params = new URLSearchParams({ period: period.toString() });
    if (organizationId) params.append('organization_id', organizationId);
    const response = await api.get(`/sla/metrics?${params.toString()}`);
    return response.data;
  }

  // Get SLA status for a ticket
  async getTicketSLAStatus(ticketId: string): Promise<{ success: boolean; data: SLATicketStatus | null }> {
    const response = await api.get(`/sla/tickets/${ticketId}/status`);
    return response.data;
  }

  // Pause SLA for a ticket
  async pauseSLA(ticketId: string, reason?: string): Promise<{ success: boolean }> {
    const response = await api.post(`/sla/tickets/${ticketId}/pause`, { reason });
    return response.data;
  }

  // Resume SLA for a ticket
  async resumeSLA(ticketId: string): Promise<{ success: boolean }> {
    const response = await api.post(`/sla/tickets/${ticketId}/resume`);
    return response.data;
  }

  // Initialize SLA for a ticket
  async initializeSLA(ticketId: string, policyId?: string): Promise<{ success: boolean }> {
    const response = await api.post(`/sla/tickets/${ticketId}/initialize`, { policyId });
    return response.data;
  }

  // Get breached tickets
  async getBreachedTickets(type: 'all' | 'first_response' | 'resolution' = 'all'): Promise<{ success: boolean; data: BreachedTicket[] }> {
    const response = await api.get(`/sla/breached?type=${type}`);
    return response.data;
  }

  // Get upcoming breaches
  async getUpcomingBreaches(warningMinutes: number = 30): Promise<{ success: boolean; data: BreachedTicket[] }> {
    const response = await api.get(`/sla/upcoming-breaches?warningMinutes=${warningMinutes}`);
    return response.data;
  }

  // Holidays
  async getHolidays(organizationId?: string): Promise<{ success: boolean; data: Holiday[] }> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await api.get(`/sla/holidays${params}`);
    return response.data;
  }

  async addHoliday(data: { date: string; name: string; is_recurring?: boolean }, organizationId?: string): Promise<{ success: boolean; data: Holiday }> {
    const payload = organizationId ? { ...data, organization_id: organizationId } : data;
    const response = await api.post('/sla/holidays', payload);
    return response.data;
  }

  async updateHoliday(id: string, data: { date: string; name: string; is_recurring?: boolean }): Promise<{ success: boolean; data: Holiday }> {
    const response = await api.put(`/sla/holidays/${id}`, data);
    return response.data;
  }

  async deleteHoliday(id: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/sla/holidays/${id}`);
    return response.data;
  }
}

export default new SLAService();